/**
 * A page-edge quotation.
 *
 * A matched passage is evidence, and evidence has to be visibly the source's
 * words rather than ours. The ornament rail and the reading measure do that
 * work; the accent-marked match span shows WHY this passage was returned,
 * which is the one thing a bare block quote never says.
 */
"use client";
import type { ReactElement } from "react";
import {
  CRC_ACCENT,
  CRC_R_PILL,
  CRC_TEXT,
  CRC_TEXT_FAINT,
} from "./cresultcard.tokens";

const RAIL_WIDTH = 2;
const RAIL_GAP = 11;
const DEFAULT_MEASURE_CH = 62;

interface CresultcardQuoteProps {
  /** The words leading into the match, verbatim. */
  readonly before?: string;
  /** The matched span itself. */
  readonly match: string;
  readonly after?: string;
  /** Where it came from: a section, a page, a paragraph number. */
  readonly caption: string;
  readonly measure?: number;
}

export function CresultcardQuote({
  before,
  match,
  after,
  caption,
  measure = DEFAULT_MEASURE_CH,
}: CresultcardQuoteProps): ReactElement {
  return (
    <figure
      data-el="Cresultcard/Quote/Root"
      style={{
        display: "flex",
        gap: RAIL_GAP,
        margin: 0,
        minWidth: 0,
      }}
    >
      <span
        aria-hidden
        style={{
          width: RAIL_WIDTH,
          flex: "0 0 auto",
          // A 2px-wide ornament rounded by 2px is a full capsule cap, and the
          // ladder-legal spelling of a capsule is the pill token — not a bare
          // rung that happens to equal the width.
          borderRadius: CRC_R_PILL,
          opacity: 0.5,
          backgroundColor: CRC_ACCENT,
        }}
      />
      <span
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          minWidth: 0,
        }}
      >
        <blockquote
          style={{
            margin: 0,
            maxWidth: `${String(measure)}ch`,
            fontSize: 11.5,
            fontWeight: 460,
            fontStyle: "italic",
            lineHeight: 1.55,
            color: CRC_TEXT,
          }}
        >
          {before}
          <mark
            style={{
              padding: 0,
              color: CRC_ACCENT,
              backgroundColor: "rgba(102,254,193,0.08)",
            }}
          >
            {match}
          </mark>
          {after}
        </blockquote>
        <figcaption
          style={{
            fontSize: 9.5,
            fontWeight: 560,
            color: CRC_TEXT_FAINT,
          }}
        >
          {caption}
        </figcaption>
      </span>
    </figure>
  );
}
